// ═══════════════════════════════════════════════════════════════════════════
// SICUREZZA DI CANTIERE — regole pure (ottobre 2026, piano Pro)
// ═══════════════════════════════════════════════════════════════════════════
// Decisioni di Paolo (ott 2026):
//  • Vale solo per le commesse che sono un CANTIERE (scelta alla creazione o dopo).
//  • Ogni cantiere ha un responsabile della sicurezza (predefinito in Impostazioni,
//    modificabile per commessa) che riceve subito notifica ed email.
//  • La checklist si adatta da sola: chi esegue i lavori (interno/subappalto/misto),
//    quante imprese, uomini-giorno, rischi particolari indicati dal responsabile.
//  • Gli adempimenti aperti producono un avviso ROSSO NON BLOCCANTE (commessa,
//    programmazione, giornale). Si può chiudere solo scrivendo il motivo: chi e quando
//    restano nello storico, che non si può cancellare. Se poi compaiono adempimenti
//    nuovi (es. si aggiunge un'impresa), l'avviso ritorna.
// Riferimenti: D.Lgs. 81/2008 (Titolo IV per i cantieri), CEI 11-27, DM 388/2003,
// DPR 177/2011, Accordo Stato-Regioni 22/02/2012, DI 22/01/2019, D.Lgs. 36/2023.
// La checklist aiuta a non dimenticare gli adempimenti principali: NON sostituisce la
// valutazione del datore di lavoro, dell'RSPP e dei coordinatori.
// ═══════════════════════════════════════════════════════════════════════════
import { tipoEsecuzione, impreseDaSalvare, uominiGiornoPresunti } from "./esecuzioneCommessa";

export const SOGLIA_NOTIFICA_UOMINI_GIORNO = 200; // art. 99 c.1 lett. c — stabilita dalla legge
export const SOGLIA_DUVRI_UOMINI_GIORNO = 5;      // art. 26 c.3-bis — stabilita dalla legge
export const AVVISO_LIMITI = "La checklist aiuta a non dimenticare gli adempimenti principali: non sostituisce la valutazione del datore di lavoro, dell'RSPP e dei coordinatori per la sicurezza.";

/** Domande sul cantiere a cui risponde il responsabile (tutte sì/no). */
export const DOMANDE = [
  { id: "noiEseguiamo",            gruppo: "imprese", testo: "La vostra impresa esegue lavorazioni in cantiere (non solo direzione e presidio)?" },
  { id: "altreImpreseCommittente", gruppo: "imprese", testo: "Nel cantiere lavorano anche altre imprese incaricate direttamente dal committente?" },
  { id: "sedeCommittenteAttiva",   gruppo: "imprese", testo: "Si lavora dentro una sede del committente con attività in corso (stabilimento, uffici, negozio…)?" },
  { id: "appaltoPubblico",         gruppo: "imprese", testo: "È un appalto pubblico?" },
  { id: "elettrici",  gruppo: "rischi", testo: "Lavori elettrici (su impianti o in loro vicinanza)" },
  { id: "quota",      gruppo: "rischi", testo: "Lavori in quota oltre 2 metri" },
  { id: "ponteggio",  gruppo: "rischi", testo: "Ponteggio" },
  { id: "ple",        gruppo: "rischi", testo: "Piattaforme di lavoro elevabili (PLE)" },
  { id: "scavi",      gruppo: "rischi", testo: "Scavi oltre 1,5 metri" },
  { id: "confinati",  gruppo: "rischi", testo: "Spazi confinati o sospetti di inquinamento" },
  { id: "amianto",    gruppo: "rischi", testo: "Possibile presenza di amianto" },
  { id: "stradali",   gruppo: "rischi", testo: "Lavori su strada aperta al traffico" },
];

/** Risposte di partenza, prima che il responsabile le confermi. */
export function configIniziale(project) {
  const c = {};
  DOMANDE.forEach(d => { c[d.id] = false; });
  c.noiEseguiamo = tipoEsecuzione(project) !== "subappalto";
  return c;
}

/** Contesto da cui dipende la checklist. */
export function contesto(project, config = {}) {
  const cfg = { ...configIniziale(project), ...(config || {}) };
  const tipo = tipoEsecuzione(project);
  const imprese = impreseDaSalvare(tipo, project?.impreseEsterne);
  const ug = uominiGiornoPresunti(project);
  const nEsecutrici = imprese.length + (cfg.noiEseguiamo ? 1 : 0) + (cfg.altreImpreseCommittente ? 1 : 0);
  return {
    cfg, tipo, imprese, ug,
    nEsecutrici,
    piuImprese: nEsecutrici >= 2,
    notificaDovuta: nEsecutrici >= 2 || ug.totale >= SOGLIA_NOTIFICA_UOMINI_GIORNO,
  };
}

const V = (id, gruppo, titolo, rif, dettaglio = "") => ({ id, gruppo, titolo, rif, dettaglio });

export const GRUPPI = {
  impresa:      "La vostra impresa",
  cantiere:     "Cantiere",
  coordinamento:"Committente e coordinamento",
  esterne:      "Imprese esterne (impresa affidataria)",
  rischi:       "Rischi particolari",
  azienda:      "Procedure della vostra azienda",
};

/**
 * Voci della checklist per questo cantiere. vociAzienda: voci aggiunte in Impostazioni
 * ({ id, titolo, dettaglio }), valide per tutti i cantieri.
 */
export function vociChecklist(project, config, vociAzienda = []) {
  const x = contesto(project, config);
  const c = x.cfg;
  const v = [];
  // ── La vostra impresa (se esegue lavorazioni) ──
  if (c.noiEseguiamo) {
    v.push(V("pos", "impresa", "POS (Piano Operativo di Sicurezza) redatto per questo cantiere", "D.Lgs. 81/08 art. 89, 96 c.1 lett. g, All. XV"));
    if (x.piuImprese || x.imprese.length > 0) v.push(V("pos_trasmesso", "impresa", "POS consegnato prima dell'inizio dei lavori (al coordinatore o all'impresa affidataria)", "art. 101 c.3"));
    v.push(V("formazione", "impresa", "Formazione dei lavoratori impiegati verificata (generale, specifica, aggiornamenti)", "art. 37"));
    v.push(V("idoneita", "impresa", "Idoneità sanitaria alla mansione dei lavoratori impiegati verificata", "art. 41"));
    v.push(V("dpi", "impresa", "DPI consegnati ai lavoratori, con consegna registrata", "art. 18 c.1 lett. d, art. 77"));
    v.push(V("attrezzature", "impresa", "Attrezzature conformi e con le verifiche periodiche in regola", "art. 70-71"));
    v.push(V("emergenze", "impresa", "Addetti al primo soccorso e antincendio presenti; cassetta di primo soccorso in cantiere", "art. 18 c.1 lett. b, art. 45-46, DM 388/2003"));
  }
  // ── Cantiere (sempre) ──
  v.push(V("preposto", "cantiere", "Preposto di cantiere individuato", "art. 18 c.1 lett. b-bis, art. 19"));
  v.push(V("tesserino", "cantiere", "Tessera di riconoscimento con fotografia per tutti i lavoratori presenti", "art. 18 c.1 lett. u, art. 26 c.8, art. 21"));
  v.push(V("recinzione", "cantiere", "Cantiere recintato e segnaletica di sicurezza esposta", "art. 109, art. 163"));
  // ── Committente e coordinamento ──
  if (x.piuImprese) {
    v.push(V("csp_cse", "coordinamento", "Nominativi dei coordinatori per la sicurezza (CSP/CSE) ricevuti dal committente", "art. 90 c.3-4, c.7 e c.11", "Con più imprese esecutrici, anche non contemporanee, il committente nomina il CSP in fase di progettazione e il CSE prima dell'affidamento dei lavori. Nei lavori privati non soggetti a permesso di costruire e di importo fino a 100.000 € le funzioni del CSP le svolge il CSE (art. 90 c.11)."));
    v.push(V("psc", "coordinamento", "PSC (Piano di Sicurezza e Coordinamento) ricevuto e accettato", "art. 100, 101"));
    v.push(V("riunione", "coordinamento", "Riunione di coordinamento con il CSE svolta (verbale)", "art. 92 c.1 lett. c"));
  }
  if (x.notificaDovuta) {
    v.push(V("notifica", "coordinamento", "Copia della notifica preliminare affissa in cantiere", "art. 99",
      x.piuImprese ? "Dovuta perché lavorano più imprese." : `Dovuta perché i lavori superano ${SOGLIA_NOTIFICA_UOMINI_GIORNO} uomini-giorno.`));
  }
  if (!x.piuImprese && c.sedeCommittenteAttiva) {
    v.push(V("duvri", "coordinamento", "Informazioni sui rischi dell'ambiente di lavoro ricevute dal committente (DUVRI se dovuto)", "art. 26 c.1 lett. b, c.3, c.3-bis",
      `Il DUVRI è di norma dovuto se i lavori superano ${SOGLIA_DUVRI_UOMINI_GIORNO} uomini-giorno o comportano rischi particolari.`));
  }
  // ── Imprese esterne ──
  if (x.imprese.length > 0) {
    x.imprese.forEach(i => {
      v.push(V("itp_" + i.id, "esterne", `Idoneità tecnico-professionale di ${i.nome} verificata (visura, DURC, DVR, nomine, formazione)`, "art. 97 c.2, All. XVII"));
      v.push(V("posest_" + i.id, "esterne", `POS di ${i.nome} ricevuto e verificato per congruenza con il vostro`, "art. 97 c.3 lett. b, art. 101 c.3"));
    });
    v.push(V("affidataria", "esterne", "Coordinamento delle imprese esecutrici e verifica delle condizioni di sicurezza dei lavori affidati", "art. 95-97"));
    v.push(V("formaz_affidataria", "esterne", "Datore di lavoro, dirigenti e preposti dell'impresa affidataria con la formazione specifica", "art. 97 c.3-ter"));
    if (c.appaltoPubblico) v.push(V("autorizz_sub", "esterne", "Autorizzazione al subappalto della stazione appaltante", "D.Lgs. 36/2023 art. 119"));
  }
  // ── Rischi particolari ──
  if (c.elettrici) {
    v.push(V("pes_pav", "rischi", "Personale per lavori elettrici designato per iscritto (PES/PAV/PEI)", "CEI 11-27, CEI EN 50110-1, art. 82"));
    v.push(V("piano_elettrico", "rischi", "Piano di lavoro e consegna dell'impianto per i lavori elettrici", "CEI 11-27"));
  }
  if (c.quota) v.push(V("quota", "rischi", "Lavori in quota: protezioni collettive o DPI anticaduta, con addestramento", "art. 107-115, art. 77 c.5"));
  if (c.ponteggio) v.push(V("pimus", "rischi", "Ponteggio: PiMUS redatto e montatori formati", "art. 134, 136, All. XXI"));
  if (c.ple) v.push(V("ple", "rischi", "PLE: operatori abilitati e verifica periodica della macchina", "Accordo Stato-Regioni 22/02/2012, art. 71 c.11"));
  if (c.scavi) v.push(V("scavi", "rischi", "Scavi oltre 1,5 m: armature o sbadacchiature", "art. 118-119"));
  if (c.confinati) v.push(V("confinati", "rischi", "Spazi confinati: imprese e lavoratori qualificati, procedura di lavoro", "DPR 177/2011"));
  if (c.amianto) v.push(V("amianto", "rischi", "Amianto: piano di lavoro presentato all'ASL prima dei lavori", "art. 256"));
  if (c.stradali) v.push(V("stradali", "rischi", "Lavori su strada: segnaletica temporanea e addetti formati", "DI 22/01/2019"));
  // ── Voci aggiunte dall'azienda ──
  (vociAzienda || []).filter(a => a && String(a.titolo || "").trim()).forEach(a => {
    v.push(V("az_" + a.id, "azienda", String(a.titolo).trim(), "Procedura aziendale", a.dettaglio || ""));
  });
  return v;
}

/** Stato di una voce: "daFare" | "fatto" | "nonApplicabile". */
export function statoVoce(stati, id) {
  const s = stati?.[id]?.stato;
  return s === "fatto" || s === "nonApplicabile" ? s : "daFare";
}

/** Riepilogo della checklist. */
export function riepilogoSicurezza(voci, stati) {
  const aperte = voci.filter(v => statoVoce(stati, v.id) === "daFare");
  return {
    totale: voci.length,
    fatte: voci.filter(v => statoVoce(stati, v.id) === "fatto").length,
    nonApplicabili: voci.filter(v => statoVoce(stati, v.id) === "nonApplicabile").length,
    aperte: aperte.length,
    idAperte: aperte.map(v => v.id),
  };
}

/**
 * Deve comparire l'avviso rosso? Sì se il cantiere non è ancora configurato, oppure se ci
 * sono voci aperte che non erano già aperte quando l'avviso è stato chiuso l'ultima volta.
 */
export function avvisoAttivo(scheda, voci) {
  if (!scheda) return { attivo: false };
  const r = riepilogoSicurezza(voci, scheda.stati || {});
  const daConfigurare = !scheda.configConfermata;
  if (!daConfigurare && r.aperte === 0) return { attivo: false, ...r, daConfigurare };
  const chiuso = scheda.avviso?.chiusoIl ? scheda.avviso : null;
  if (chiuso) {
    const giaViste = new Set(chiuso.vociAperte || []);
    const nuove = r.idAperte.filter(id => !giaViste.has(id));
    const configNuova = daConfigurare && !chiuso.eraDaConfigurare;
    if (nuove.length === 0 && !configNuova) return { attivo: false, chiuso: true, ...r, daConfigurare };
  }
  return { attivo: true, ...r, daConfigurare };
}

/** Testo dell'avviso. */
export function testoAvviso(a) {
  if (a.daConfigurare) return "Sicurezza di cantiere da impostare: l'addetto alla sicurezza deve rispondere alle domande sul cantiere.";
  return `Sicurezza di cantiere: ${a.aperte} adempiment${a.aperte === 1 ? "o" : "i"} da completare.`;
}

/** Giorni lavorativi del cantiere sopra i quali Nexiora suggerisce di trattare la commessa come cantiere. */
export const GIORNI_SUGGERIMENTO_CANTIERE = 5;

/**
 * Sezione «Sicurezza di cantiere» della Relazione della commessa (ottobre 2026).
 * Non dice mai «tutto bene»: riporta quello che risulta registrato in Nexiora, mette in
 * allerta quando qualcosa manca e ricorda sempre che la checklist non sostituisce le
 * verifiche in cantiere — chi legge non deve abbassare il livello di guardia.
 * stato: "non_cantiere" | "non_attiva" | "da_impostare" | "aperte" | "registrate"
 */
export function sezioneSicurezzaRelazione(project, scheda, vociAzienda = [], { aperta = true } = {}) {
  const avvertenza = "Questa sezione riporta ciò che è registrato in Nexiora. La checklist aiuta a non dimenticare gli adempimenti principali, ma non sostituisce la valutazione del datore di lavoro, dell'RSPP e dei coordinatori per la sicurezza: tutto va sempre verificato in cantiere.";
  if (!project?.cantiere) {
    return { stato: "non_cantiere", allerta: false, avvertenza,
      testo: "La commessa non è indicata come cantiere: in Nexiora non risulta alcuna verifica degli adempimenti di sicurezza di cantiere (Titolo IV del D.Lgs. 81/08). Se i lavori si svolgono in un cantiere temporaneo o mobile, la sicurezza di cantiere va attivata dalla commessa." };
  }
  if (!scheda) {
    return { stato: "non_attiva", allerta: true, avvertenza,
      testo: "ATTENZIONE: la commessa è un cantiere ma la sicurezza di cantiere non è stata attivata. Non risulta alcuna verifica degli adempimenti: va attivata e completata." };
  }
  const voci = vociChecklist(project, scheda.config, vociAzienda);
  const r = riepilogoSicurezza(voci, scheda.stati || {});
  const addetto = scheda.responsabileNome ? ` Addetto alla sicurezza e alla documentazione di cantiere: ${scheda.responsabileNome}.` : " L'addetto alla sicurezza non è indicato.";
  const nonApplicabili = voci.filter(v => statoVoce(scheda.stati, v.id) === "nonApplicabile").map(v => ({ titolo: v.titolo, motivo: scheda.stati?.[v.id]?.nota || "" }));
  const vociAperte = voci.filter(v => statoVoce(scheda.stati, v.id) === "daFare").map(v => v.titolo);
  const chiuso = scheda.avviso?.chiusoIl ? ` L'avviso rosso è stato chiuso${scheda.avviso.chiusoDaNome ? ` da ${scheda.avviso.chiusoDaNome}` : ""}${scheda.avviso.motivo ? ` con il motivo «${scheda.avviso.motivo}»` : ""}: chiudere l'avviso non completa gli adempimenti.` : "";
  if (!scheda.configConfermata) {
    return { stato: "da_impostare", allerta: true, avvertenza, vociAperte, nonApplicabili,
      testo: `ATTENZIONE: la sicurezza di cantiere è attiva ma non impostata: l'addetto non ha ancora risposto alle domande sul cantiere, quindi la checklist degli adempimenti non è completa.${addetto}${chiuso}` };
  }
  if (r.aperte > 0) {
    return { stato: "aperte", allerta: true, avvertenza, vociAperte, nonApplicabili,
      testo: `ATTENZIONE: ${r.aperte} adempiment${r.aperte === 1 ? "o" : "i"} di sicurezza su ${r.totale} ${r.aperte === 1 ? "risulta" : "risultano"} ancora da completare${aperta ? "" : " alla chiusura della commessa"}.${addetto}${chiuso}` };
  }
  return { stato: "registrate", allerta: false, avvertenza, vociAperte: [], nonApplicabili,
    testo: `Per i ${r.totale} adempimenti della checklist risulta registrato un esito: ${r.fatte} segnat${r.fatte === 1 ? "o" : "i"} come fatt${r.fatte === 1 ? "o" : "i"}${r.nonApplicabili ? ` e ${r.nonApplicabili} come non applicabil${r.nonApplicabili === 1 ? "e" : "i"} (motivi da verificare, elencati sotto)` : ""}.${addetto}` };
}
